<template>
    <div>
        <!-- Quality Defect list -->
        <q-table
            @request="onRequest"
            :filter="table.search"
            :rows="qualityDefects"
            :columns="table.cols"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :no-data-label="$t('No quality defect available')"
            :class="{ 'sticky-table quality-defects-table': $q.screen.gt.lg }"
            row-key="id"
            binary-state-sort
            flat
            :grid="$q.screen.lt.lg"
        >
            <!-- top bar -->
            <template v-slot:top>
                <!-- title -->
                <div class="col-12">
                    <div class="text-h4 text-weight-medium">
                        {{ $t('Quality Defects') }}
                    </div>
                    <div class="page-description">
                        {{
                            $t('Manage quality defects for {facilityName}.', {
                                facilityName: facility?.name
                            })
                        }}
                    </div>
                </div>
                <!-- search bar -->
                <div
                    class="row fit q-mb-sm"
                    :class="{
                        'wrap justify-between items-start content-start':
                            $q.screen.lt.sm
                    }"
                >
                    <q-input
                        v-model="table.search"
                        @clear="table.search = ''"
                        :placeholder="$t('Search quality defect')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        class="bg-white"
                        debounce="750"
                        clearable
                        outlined
                        dense
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <!-- "Add quality defect" button -->
                    <q-btn
                        @click="
                            $router.push({
                                name: 'quality_defect_create',
                                params: {
                                    facilityId: facility.id
                                }
                            })
                        "
                        :label="$t('Add quality defect')"
                        :disable="$userCannot('create', 'quality_defects')"
                        class="table-header__button"
                        :class="{ 'col-12': $q.screen.lt.sm }"
                        color="accent"
                        size="md"
                        id="add-quality-defect-btn"
                    />
                </div>
            </template>

            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'quality_defect_update',
                                    params: {
                                        facilityId: facility.id,
                                        qualityDefectId: props.row.id
                                    },
                                    state: {
                                        name: props.row.name,
                                        code: props.row.code,
                                        description: props.row.description
                                    }
                                })
                            "
                            :disable="$userCannot('edit', 'quality_defects')"
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        />
                        <div>
                            <q-btn
                                @click="remove(props.row)"
                                :disable="
                                    $userCannot('delete', 'quality_defects')
                                "
                                :color="
                                    $userCannot('delete', 'quality_defects')
                                        ? 'grey'
                                        : 'negative'
                                "
                                class="table__action-button"
                                icon="fas fa-trash-alt"
                                outline
                            />
                            <q-tooltip
                                v-if="$userCannot('edit', 'quality_defects')"
                            >
                                {{
                                    $t(
                                        'You do not have permission to perform this action.'
                                    )
                                }}
                            </q-tooltip>
                        </div>
                    </div>
                </q-td>
            </template>

            <!-- Card template -->
            <template v-slot:item="props">
                <defect-card
                    @edit="
                        $router.push({
                            name: 'quality_defect_update',
                            params: {
                                facilityId: facility.id,
                                qualityDefectId: props.row.id
                            },
                            state: {
                                name: props.row.name,
                                code: props.row.code,
                                description: props.row.description
                            }
                        })
                    "
                    @delete="remove(props.row)"
                    :name="props.row.name"
                    :description="props.row.description"
                    :code="props.row.code"
                />
            </template>
        </q-table>
    </div>
</template>

<script>
import DefectCard from '@components/DefectCard.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { formatQualityDefects } from '../../utils/qualityDefects.js';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';

export default {
    name: 'QualityDefectList',
    components: { DefectCard },
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Quality Defects')
            }
        ]);

        this.onRequest({
            pagination: this.table.pagination,
            filter: ''
        });
    },
    data() {
        return {
            baseURL: 'defects/',
            qualityDefects: [],
            // table configs
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true,
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('Code'),
                        sortable: true,
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description'),
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            }
        };
    },
    computed: {
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.$store.getters['facilities/getFacility'](
                this.facilityId
            );
        },
        /**
         * Constructs a URL with query parameters for fetching defect data.
         *
         * @returns {String} - The constructed URL.
         *
         * The URL has the following structure:
         * /api/v1/defects/?search=&ordering=&page=&facility=1
         *
         * Query Parameters:
         * - search: (String) The search query to filter defects.
         * - ordering: (String) The sort order of the results.
         * - page: (Number) The page number for pagination.
         * - facility: (Number) The ID of the facility to filter defects by.
         */
        url() {
            // get current pagination state attributes
            let { descending, page, sortBy } = this.table.pagination;

            // order by category name
            if (sortBy === 'category') {
                sortBy = 'category__name';
            }

            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: this.table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    facility: this.facilityId
                }
            });
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Get quality defects from the API.
         * @return {Object} - Quality defects.
         * The API response is an object with the following structure:
         * {
         *    count: 1,
         *    next: null,
         *    previous: null,
         *    results: [
         *      {
         *        id: 1,
         *        name: 'Quality defect 1',
         *        code: 'QD1',
         *        description: 'Quality defect 1 description'
         *      }
         *    ]
         * }
         */
        async getQualityDefects() {
            try {
                const response = await this.$axios.get(this.url);
                const formattedResponse = formatQualityDefects(
                    response.data.results
                );
                this.qualityDefects.push(...formattedResponse);

                // update total items count
                this.table.pagination.rowsNumber = response.data.count;
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to load quality defects. Please try again later.'
                    ),
                    error
                );
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Delete quality defect.
         * @param {Object} row - Quality defect from table row.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this quality defect?')
            ).onOk(async () => {
                // begin loading
                this.beginLoading(`${this.$t('Deleting quality defect')}...`);
                try {
                    await this.$axios.delete(`${this.baseURL}${row.id}/`);

                    // remove quality defect from product list
                    this.qualityDefects = this.qualityDefects.filter(
                        qualityDefect => qualityDefect.id !== row.id
                    );

                    // notify user
                    this.$notifySuccess(this.$t('Quality defect deleted'));
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to remove quality defect. Please try again later.'
                        ),
                        error
                    );
                } finally {
                    // stop loading
                    this.stopLoading();
                }
            });
        },
        /**
         * Fetch data from the API. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         */
        async onRequest(props) {
            // update pagination object
            this.table.pagination = props.pagination;

            // begin loading
            this.beginLoading(`${this.$t('Loading quality defects')}...`);

            // clear existing data
            this.qualityDefects = [];

            // fetch data from the API
            await this.getQualityDefects();
        }
    }
};
</script>

<style lang="scss" scoped>
// align table top with cards
:deep(.q-table__top) {
    padding: 0px;
}

.quality-defects-table {
    height: 75vh;
}
</style>
